import React from "react";
import { IconCalendar, IconCalendarX, IconClock, IconHistory, IconMapPin } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import type { BookingDetails } from "@/app/booking-flow/publicBookingFlowTypes";
import { formatAppointmentDate, formatAppointmentTime, formatCheckInDateTime } from "../booking-flow/checkInTime";

// What the manage page can say about a booking it found (AB#624). The lookup returns bookings of
// any status, so the page has to tell them apart rather than calling everything upcoming:
// - "cancelled": Cal says cancelled (or rejected). Wins over "past" — a cancelled appointment whose
//   time has also gone by was still cancelled, and that is the thing worth saying.
// - "past": its start time has gone by. Nothing to cancel any more.
// - "upcoming": accepted and still ahead — the only state that offers Cancel.
// - "unconfirmed": ahead, but in some other live Cal status (pending). Shown, not cancellable.
export type ManagedBookingState = "upcoming" | "unconfirmed" | "cancelled" | "past";

const CANCELLED_STATUSES = new Set(["cancelled", "rejected"]);

// `nowMs` is passed in rather than read here, so the page decides when "now" is and tests can pin
// it. Past is a comparison of instants, which no time zone changes; the Pacific zone matters only
// for how the date is written, and the formatters handle that.
export function managedBookingState(booking: BookingDetails, nowMs: number): ManagedBookingState {
  const status = booking.status.toLowerCase();
  if (CANCELLED_STATUSES.has(status)) return "cancelled";

  const start = Date.parse(booking.start);
  if (!Number.isNaN(start) && start <= nowMs) return "past";

  return status === "accepted" ? "upcoming" : "unconfirmed";
}

interface ManagedBookingCardProps {
  booking: BookingDetails;
  // The latest note staff wrote for the attendee, as plain text. Shown only on a cancelled booking.
  customerNote?: string | null;
  nowMs: number;
  onCancel: () => void;
  onBack: () => void;
  // The cancel dialog, which belongs to the page and is only reachable from an upcoming booking.
  children?: React.ReactNode;
}

function SiteLine({ booking }: { booking: BookingDetails }) {
  if (!booking.siteName && !booking.location) return null;

  return (
    <div className="flex gap-3">
      <IconMapPin className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
      <div className="flex flex-col">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Location</span>
        <span className="font-medium text-sm mt-0.5">
          {booking.siteName}
          {booking.siteName && booking.location ? " — " : ""}
          {booking.location}
        </span>
      </div>
    </div>
  );
}

function DateLine({ booking }: { booking: BookingDetails }) {
  return (
    <div className="flex gap-3">
      <IconCalendar className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
      <div className="flex flex-col">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Date</span>
        <span className="font-medium text-sm mt-0.5">
          {formatAppointmentDate(booking.start)} at {formatAppointmentTime(booking.start)}
        </span>
      </div>
    </div>
  );
}

export function ManagedBookingCard({ booking, customerNote, nowMs, onCancel, onBack, children }: ManagedBookingCardProps) {
  const state = managedBookingState(booking, nowMs);

  const backButton = (
    <Button type="button" variant="ghost" className="w-full text-slate-400" onClick={onBack}>
      Back
    </Button>
  );

  if (state === "cancelled" || state === "past") {
    const cancelled = state === "cancelled";
    const note = cancelled ? customerNote?.trim() : undefined;

    return (
      <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
        <CardHeader className="text-center px-4 space-y-4">
          <div className="mx-auto w-16 h-16 bg-slate-100 flex items-center justify-center rounded-full">
            {cancelled ? <IconCalendarX className="w-8 h-8 text-slate-500" /> : <IconHistory className="w-8 h-8 text-slate-500" />}
          </div>
          <CardTitle className="text-2xl font-semibold text-slate-900">
            {cancelled ? "Appointment Cancelled" : "This appointment has passed"}
          </CardTitle>
          <CardDescription className="text-slate-500 text-base">
            {cancelled
              ? "This appointment was cancelled. If you need energy assistance, please schedule a new appointment."
              : "The time for this appointment has gone by. If you still need energy assistance, please schedule a new appointment."}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-slate-50 p-5 rounded-lg border space-y-4">
            <DateLine booking={booking} />
            <SiteLine booking={booking} />
          </div>

          {note && (
            <div className="space-y-1" data-testid="customer-note">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Note from our staff</span>
              <p className="text-sm text-slate-700 whitespace-pre-line">{note}</p>
            </div>
          )}
        </CardContent>

        <CardFooter className="px-4 pb-6 flex flex-col gap-2">{backButton}</CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
      <CardHeader className="text-center px-4">
        <CardTitle className="text-2xl font-semibold text-slate-900">Your Appointment</CardTitle>
        <CardDescription className="text-slate-500">
          Here are the details of your upcoming appointment.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="bg-slate-50 p-5 rounded-lg border space-y-4">
          <div className="flex gap-3">
            <IconClock className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex flex-col">
              <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">Check-In Time</span>
              <span className="font-bold text-base text-slate-900 mt-0.5">
                {formatCheckInDateTime(booking.start)}
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <IconCalendar className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
            <div className="flex flex-col">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">Appointment Time</span>
              <span className="font-medium text-sm mt-0.5">{formatAppointmentTime(booking.start)}</span>
            </div>
          </div>

          <SiteLine booking={booking} />
        </div>
      </CardContent>

      <CardFooter className="px-4 pb-6 flex flex-col gap-2">
        {state === "upcoming" && (
          <Button type="button" variant="destructive" className="w-full text-base h-12" onClick={onCancel}>
            Cancel Appointment
          </Button>
        )}
        {backButton}
      </CardFooter>

      {state === "upcoming" && children}
    </Card>
  );
}
